import React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSettings } from '../../../contexts/SettingsContext';

const EnvironmentInfo = ({ environmentData, loading, error }) => {
    const { settings, clientMaintenance } = useSettings();
    if (loading) {
        return (
            <div className="bg-bgSecondary shadow-neo-card p-4 rounded flex flex-col">
                <h4 className="font-medium mb-3 text-primary">Environment Information</h4>
                <div className="flex items-center justify-center p-4">
                    <div className="text-font-secondary">Loading environment data...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-bgSecondary shadow-neo-card p-4 rounded flex flex-col">
                <h4 className="font-medium mb-3 text-primary">Environment Information</h4>
                <div className="flex items-center justify-center p-4">
                    <div className="text-error text-sm">{error}</div>
                </div>
            </div>
        );
    }

    if (!environmentData) {
        return (
            <div className="bg-bgSecondary shadow-neo-card p-4 rounded flex flex-col">
                <h4 className="font-medium mb-3 text-primary">Environment Information</h4>
                <div className="flex items-center justify-center p-4">
                    <div className="text-font-secondary text-sm">No environment data available</div>
                </div>
            </div>
        );
    }

    const formatUptime = (uptime) => {
        if (typeof uptime === 'number') {
            const hours = Math.floor(uptime / 3600);
            const minutes = Math.floor((uptime % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
        return uptime || 'Unknown';
    };

    const formatMemoryUsage = (memory) => {
        if (typeof memory === 'number') {
            return `${Math.round(memory / 1024 / 1024)} MB`;
        }
        return memory || 'Unknown';
    };

    const getLoggingStatus = () => {
        const debugStatus = settings.debug_logging ? '✓' : '✗';
        const apiStatus = settings.api_logging ? '✓' : '✗';
        return `Debug ${debugStatus} API ${apiStatus}`;
    };

    const getMaintenanceStatus = () => {
        if (settings.maintenance_mode) {
            if (clientMaintenance.length > 0) {
                return `Client (${clientMaintenance.length} affected)`;
            }
            return 'Global';
        }
        return 'None';
    };

    const environmentItems = [
        {
            label: 'Environment Type',
            value: environmentData.environment_type || 'Development',
            icon: 'mdi:server'
        },
        {
            label: 'App Version',
            value: environmentData.app_version || '1.0.0',
            icon: 'mdi:tag'
        },
        {
            label: 'Node Version',
            value: environmentData.node_version || '18.17.0',
            icon: 'mdi:nodejs'
        },
        {
            label: 'Uptime',
            value: formatUptime(environmentData.uptime),
            icon: 'mdi:clock'
        },
        {
            label: 'Memory Usage',
            value: formatMemoryUsage(environmentData.memory_usage),
            icon: 'mdi:memory'
        },
        {
            label: 'CPU Usage',
            value: environmentData.cpu_usage ? `${environmentData.cpu_usage}%` : 'Unknown',
            icon: 'mdi:cpu-64-bit'
        },
        {
            label: 'Logging Status',
            value: getLoggingStatus(),
            icon: 'mdi:file-document-outline',
            status: settings.debug_logging || settings.api_logging ? 'active' : 'inactive'
        },
        {
            label: 'Maintenance Mode',
            value: getMaintenanceStatus(),
            icon: 'ix:maintenance',
            status: settings.maintenance_mode ? 'active' : 'inactive'
        }
    ];

    return (
        <div className="card-container flex flex-col gap-3">
            <h4 className="font-semibold text-secondary">Environment Information</h4>
            <div className="flex flex-col flex-1 gap-3 text-sm px-2">
                {environmentItems.map((item, index) => (
                    <React.Fragment key={item.label}>
                        {index > 0 && <hr className="border-bgAccent" />}
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5">
                                <Icon 
                                    icon={item.icon} 
                                    className={`text-xl ${
                                        item.status === 'active' 
                                            ? 'text-green-500' 
                                            : item.status === 'inactive' 
                                                ? 'text-gray-500' 
                                                : 'text-primary'
                                    }`} 
                                />
                                <span className="font-semibold">{item.label}:</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`font-semibold ${
                                    item.status === 'active' 
                                        ? 'text-green-500' 
                                        : item.status === 'inactive' 
                                            ? 'text-gray-500' 
                                            : 'text-secondary'
                                }`}>
                                    {item.value}
                                </span>
                                {item.status && (
                                    <div className={`w-2 h-2 rounded-full ${
                                        item.status === 'active' 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-400'
                                    }`} title={`${item.status}`}></div>
                                )}
                            </div>
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default EnvironmentInfo;
